import { Alert } from '@catering/ui';
import { useNavigate } from '@tanstack/react-router';
import type { JSX, ReactNode } from 'react';
import { useEffect } from 'react';

import { ApiError } from '../api/client';
import { useSessionStore } from '../auth/session-store';
import { usePortalMe } from './use-portal-me';

const BACKOFFICE_URL: string =
  (import.meta.env.VITE_BACKOFFICE_URL as string | undefined) ?? 'http://localhost:5173';

/**
 * `client-portal/design.md` §6.3: the reactive half of the session guard.
 *
 * `router.tsx`'s `beforeLoad` only runs on navigation, so it covers the
 * first load. A 401 that survives `apiRequest`'s own single retry clears
 * the session from underneath an already-mounted page — this component
 * subscribes to the store directly so that case redirects too, without
 * requiring the user to navigate anywhere first.
 *
 * `GET /portal/me` (`portal-access/spec.md` → "Login exitoso sin acceso
 * vigente resuelve alcance NONE") now distinguishes `NONE` (never
 * granted access) from `EXPIRED` (access that lapsed) — both mean "no
 * active access" here, replacing the earlier structural-404-only probe.
 */
export function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element | null {
  const navigate = useNavigate();
  const session = useSessionStore((state) => state.session);
  const isRestoring = useSessionStore((state) => state.isRestoring);
  const me = usePortalMe(session !== null);

  useEffect(() => {
    if (!isRestoring && session === null) {
      // Exactly one redirect per loss of session — there is nothing here
      // that retries or re-triggers itself.
      void navigate({ to: '/login' });
    }
  }, [isRestoring, session, navigate]);

  if (isRestoring || session === null || me.isPending) {
    return null;
  }

  /**
   * `GET /portal/me` answers 404 only when the account is internal
   * (back-office) rather than a customer contact — the endpoint is
   * portal-only and rejects it structurally, before any
   * NONE/EXPIRED access-scope resolution runs. Distinct from every other
   * error and from `NONE`/`EXPIRED`: those are a customer without active
   * access, this is staff at the wrong URL.
   */
  if (me.isError && me.error instanceof ApiError && me.error.status === 404) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4">
        <Alert tone="warning">
          Esta cuenta es del back-office. Ingresá desde el{' '}
          <a href={BACKOFFICE_URL} className="font-medium underline">
            panel de administración
          </a>
          .
        </Alert>
      </div>
    );
  }

  if (me.isError || me.data.status !== 'ACTIVE') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4">
        <Alert tone="warning">Sin acceso activo. Contactá a tu proveedor.</Alert>
      </div>
    );
  }

  return <>{children}</>;
}
